// Home page. Auto-provisioned org per user (one per account for now).
// The Captain's personal org is created server-side on sign-in; this
// page just lists its workspaces and lets the Captain rename the org
// inline. New-workspace creation stays — a workspace is the unit of
// work inside the org.

import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { SidebarShell } from "../layout/SidebarShell";
import {
  ApiError,
  createWorkspace,
  extractRows,
  listOrgWorkspaces,
  listOrgs,
  renameOrg,
} from "../api/client";
import { WaytideGreeter } from "./WaytideGreeter";

interface Org {
  org_id: number;
  slug: string;
  name: string;
}

interface Workspace {
  workspace_id: number;
  org_id: number;
  slug: string;
  name: string;
}

function rowToOrg(row: unknown[]): Org | null {
  const org_id = typeof row[0] === "number" ? row[0] : Number(row[0]);
  const slug = typeof row[1] === "string" ? row[1] : "";
  const name = typeof row[2] === "string" ? row[2] : "";
  if (!org_id || !slug || !name) return null;
  return { org_id, slug, name };
}

function rowToWorkspace(row: unknown[]): Workspace | null {
  const workspace_id = typeof row[0] === "number" ? row[0] : Number(row[0]);
  const org_id = typeof row[1] === "number" ? row[1] : Number(row[1]);
  const slug = typeof row[2] === "string" ? row[2] : "";
  const name = typeof row[3] === "string" ? row[3] : "";
  if (!workspace_id || !org_id || !slug || !name) return null;
  return { workspace_id, org_id, slug, name };
}

function slugify(name: string): string {
  return name
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
}

export function HomePage() {
  const navigate = useNavigate();
  const [org, setOrg] = useState<Org | null>(null);
  const [workspaces, setWorkspaces] = useState<Workspace[]>([]);
  const [editingOrgName, setEditingOrgName] = useState(false);
  const [orgNameDraft, setOrgNameDraft] = useState("");
  const [newWorkspaceName, setNewWorkspaceName] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  async function refreshOrg() {
    try {
      const result = await listOrgs();
      const list = extractRows(result)
        .map(rowToOrg)
        .filter((o): o is Org => o !== null);
      // Captain's personal org is server-auto-provisioned on sign-in.
      // Pick the first row — one org per user for now.
      if (list[0]) setOrg(list[0]);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Could not load org");
    }
  }

  async function refreshWorkspaces(orgId: number) {
    try {
      const result = await listOrgWorkspaces(orgId);
      const list = extractRows(result)
        .map(rowToWorkspace)
        .filter((p): p is Workspace => p !== null);
      setWorkspaces(list);
    } catch (err) {
      setError(
        err instanceof ApiError ? err.message : "Could not load workspaces",
      );
    }
  }

  useEffect(() => {
    void refreshOrg();
  }, []);

  useEffect(() => {
    if (org) void refreshWorkspaces(org.org_id);
  }, [org]);

  async function submitRenameOrg(e: React.FormEvent) {
    e.preventDefault();
    if (!org) return;
    const name = orgNameDraft.trim();
    if (!name) {
      setError("Org name cannot be empty.");
      return;
    }
    setBusy(true);
    setError("");
    try {
      await renameOrg(org.org_id, name);
      setOrg({ ...org, name });
      setEditingOrgName(false);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Could not rename org");
    } finally {
      setBusy(false);
    }
  }

  async function submitCreateWorkspace(e: React.FormEvent) {
    e.preventDefault();
    if (!org) return;
    const name = newWorkspaceName.trim();
    const slug = slugify(name);
    if (!name || !slug) {
      setError("Workspace name must contain at least one letter or number.");
      return;
    }
    setError("");
    setBusy(true);
    try {
      await createWorkspace(org.org_id, slug, name);
      void import("../observability/posthog").then((m) =>
        m.capture("workspace_created", { org_id: org.org_id, slug }),
      );
      setNewWorkspaceName("");
      await refreshWorkspaces(org.org_id);
    } catch (err) {
      setError(
        err instanceof ApiError ? err.message : "Could not create workspace",
      );
    } finally {
      setBusy(false);
    }
  }

  return (
    <SidebarShell>
      <div className="mx-auto flex max-w-6xl flex-col gap-6 p-8 pt-16">
        <WaytideGreeter
          agent="vela"
          dismissKey="home-welcome"
          message={
            <>
              Welcome aboard. I'm here to keep you oriented while you're
              building. Create a workspace, connect a repo, build your team.
              When you need a steer, @mention me in the workspace main chat.
            </>
          }
        />

        <section className="flex flex-col gap-6">
          <header className="flex items-baseline gap-3">
            <span className="text-xs text-muted">Your org</span>
            {org ? (
              editingOrgName ? (
                <form
                  onSubmit={submitRenameOrg}
                  className="flex items-baseline gap-2"
                  data-testid="rename-org-form"
                >
                  <input
                    autoFocus
                    value={orgNameDraft}
                    onChange={(e) => setOrgNameDraft(e.target.value)}
                    onBlur={() => {
                      if (orgNameDraft.trim() === "") setEditingOrgName(false);
                    }}
                    onKeyDown={(e) => {
                      if (e.key === "Escape") {
                        e.preventDefault();
                        setEditingOrgName(false);
                      }
                    }}
                    placeholder="Org name"
                    maxLength={80}
                    disabled={busy}
                    className="rounded-md border border-edge bg-surface-glass px-2 py-1 text-base text-primary outline-none focus:border-accent"
                    data-testid="rename-org-input"
                  />
                  <button
                    type="submit"
                    disabled={busy}
                    className="cursor-pointer rounded-md bg-accent px-2 py-1 text-xs font-semibold text-[var(--wt-selection-text)] transition-opacity hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-40"
                    data-testid="rename-org-submit"
                  >
                    Save
                  </button>
                </form>
              ) : (
                <button
                  type="button"
                  onClick={() => {
                    setOrgNameDraft(org.name);
                    setEditingOrgName(true);
                    setError("");
                  }}
                  className="cursor-pointer rounded-md px-1 py-0.5 text-left text-base text-primary transition-colors hover:bg-surface-raised"
                  data-testid="rename-org-trigger"
                  title="Rename org"
                >
                  {org.name}
                </button>
              )
            ) : (
              <span className="text-base text-faint">resolving…</span>
            )}
          </header>

          <h2 className="text-xs text-muted">Workspaces</h2>

          <ul className="grid grid-cols-2 gap-3" data-testid="workspaces-list">
            {workspaces.map((w) => (
              <li key={w.workspace_id}>
                <button
                  onClick={() => navigate(`/workspaces/${w.workspace_id}`)}
                  className="flex w-full cursor-pointer flex-col items-start gap-1 rounded-panel border border-edge bg-surface-glass p-4 text-left transition-colors hover:border-accent"
                  data-testid={`workspace-${w.slug}`}
                >
                  <span className="text-lg text-primary">{w.name}</span>
                  <span className="text-xs text-faint">@{w.slug}</span>
                </button>
              </li>
            ))}
            {org && workspaces.length === 0 && (
              <li className="col-span-2 rounded-panel border border-edge bg-surface-raised px-4 py-3 text-sm text-muted">
                No workspaces yet. Create your first below.
              </li>
            )}
          </ul>

          {org && (
            <form
              onSubmit={submitCreateWorkspace}
              className="flex flex-col gap-2 rounded-panel border border-edge bg-surface-glass p-4 backdrop-blur"
            >
              <h3 className="text-xs text-muted">New workspace</h3>
              <input
                value={newWorkspaceName}
                onChange={(e) => setNewWorkspaceName(e.target.value)}
                placeholder="Display name"
                required
                className="rounded-md glass-blur border border-edge bg-surface-glass px-3 py-2 text-sm outline-none focus:border-accent"
                data-testid="new-workspace-name"
              />
              {newWorkspaceName.trim() && (
                <p className="px-1 font-code text-xs text-faint">
                  @{slugify(newWorkspaceName.trim()) || "(needs letters or numbers)"}
                </p>
              )}
              <button
                type="submit"
                disabled={busy}
                className="cursor-pointer rounded-md bg-accent px-3 py-2 text-xs font-semibold text-[var(--wt-selection-text)] transition-opacity hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-40"
                data-testid="new-workspace-submit"
              >
                Create workspace
              </button>
            </form>
          )}
        </section>
      </div>

      {error && (
        <div
          className="fixed bottom-6 left-1/2 -translate-x-1/2 rounded-md border border-danger bg-surface-raised px-4 py-2 text-sm text-danger shadow-[var(--wt-shadow)]"
          data-testid="home-error"
        >
          {error}
        </div>
      )}
    </SidebarShell>
  );
}
